import { CloseCircleOutlined, FileAddOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Form, Input, message, Modal, Row } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WIDTH_MODAL } from '../../../constants/common';
import { MISC } from '../../../constants/define/translation';
import { MESSAGE_ERROR } from '../../../constants/message';
import {
  ExternalToolRecord,
  postSaveExternalToolRepository,
} from '../../../repository/mytm/external-tool/externalToolRepository';
import { appStyles } from '../../../styles';
import { ButtonActionPage } from '../../atoms';
import { TitleModal } from '../../molecules/modal/TitleModal';

interface ExternalToolsModalProps {
  isOpenModal: boolean;
  onClose: () => void;
  record?: ExternalToolRecord;
  onRefresh: () => void;
  onSetLoading: (loading: boolean) => void;
  onSetCurrentRecord: (record?: ExternalToolRecord) => void;
}

interface FormValue {
  name?: string;
  toolUrl?: string;
}

export const ExternalToolsModal = (props: ExternalToolsModalProps) => {
  const { isOpenModal, onClose, record, onRefresh, onSetLoading, onSetCurrentRecord } = props;
  const { t } = useTranslation(MISC);
  const [form] = Form.useForm();
  const [formValue] = useState<FormValue>({ name: undefined, toolUrl: undefined });

  const onFinish = useCallback(
    async (dataForm: FormValue) => {
      onSetLoading(true);
      try {
        await postSaveExternalToolRepository({
          id: record && record?.id,
          ...dataForm,
        });
        onRefresh();
        onClose();
        onSetCurrentRecord();
        if (record && record?.id) {
          message.success(t('message.updateExternalToolSuccess'));
        } else {
          message.success(t('message.createExternalToolSuccess'));
        }
      } catch (error) {
        message.error(MESSAGE_ERROR.M02);
      } finally {
        onSetLoading(false);
      }
    },
    [form, record, onClose, onRefresh],
  );

  useEffect(() => {
    if (record) {
      const formDataValue = { name: record.name, toolUrl: record.toolUrl };
      form.setFieldsValue(formDataValue);
    } else {
      form.setFieldsValue(formValue);
    }
  }, [record]);
  return (
    <StyledModal
      title={<TitleModal title={record ? t('updatePromotion') : t('createPromotion')} />}
      centered
      visible={isOpenModal}
      width={WIDTH_MODAL / 1.5}
      footer={null}
      onCancel={() => {
        onClose();
      }}
      maskClosable={false}
      closeIcon={<CloseCircleOutlined />}
      forceRender
    >
      <SwitchContainer>
        <Form form={form} onFinish={onFinish} layout="vertical" initialValues={formValue} name="form-externalTool">
          <Form.Item name="name" label={t('form.name')} rules={[{ required: true, message: t('validate.name') }]}>
            <Input placeholder={t('form.name')} maxLength={255} />
          </Form.Item>
          <Form.Item
            name="toolUrl"
            label={t('form.toolUrl')}
            rules={[{ required: true, message: t('validate.toolUrlBlank') }, { max: 255 }]}
          >
            <Input placeholder={t('form.toolUrl')} />
          </Form.Item>
          <Row style={{ justifyContent: 'flex-end', paddingTop: 20 }}>
            <BtnContainer>
              <ButtonActionPage
                text={record ? t('buttonUpdate') : t('buttonCreate')}
                id="1"
                className="btn-submit-form-home"
                htmlType="submit"
                icon={!record ? <FileAddOutlined /> : undefined}
              />
            </BtnContainer>
          </Row>
        </Form>
      </SwitchContainer>
    </StyledModal>
  );
};

const SwitchContainer = styled.div`
  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & .form-promotion-container {
    padding: 1.5rem;

    .form-promotion__description {
      resize: none;
    }
  }
`;

const StyledModal = styled(Modal)`
  & > .ant-modal-content {
    ${appStyles.commonModalContentStyle}
  }

  .site-card-wrapper {
    ${appStyles.background.modalBgGray}
    & .card-item {
      text-align: center;
      padding: 1rem;
    }
  }

  & h4.ant-typography {
    ${appStyles.color.black}
  }

  .ant-modal-header {
    padding-left: 0;
  }

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }
`;

const BtnContainer = styled.div`
  & .btn-submit-form-home {
    width: 160px;
  }
`;
